import {
  Component,
  createSignal,
  createMemo,
  createEffect,
  Show,
  For,
  onCleanup,
} from 'solid-js';
import { defineCustomElements } from '@trimble-oss/modus-web-components/loader';
import { TooltipDelayed } from '../tooltipDelayed/TooltipDelayed';

// Ensure modus web components are defined
defineCustomElements();

// Import types
import type {
  DateTimeRange,
  DiscreteDateTime,
  CalendarMode,
  CalendarProps,
  CalendarItem,
  DateRange,
} from './types';
import { isDiscreteDateTime } from './types';

// Re-export types for external use
export type {
  DateTimeRange,
  DiscreteDateTime,
  CalendarMode,
  CalendarProps,
  CalendarItem,
  DateRange,
};

// Constants for magic numbers
const DECEMBER_MONTH = 11;
const CALENDAR_GRID_SIZE = 42;
const DEFAULT_YEAR_RANGE_OFFSET = 5; // Default years before/after current year for year dropdown

// Icon size constant for tc-icons
const TC_ICON_SIZE = '16px';

// Icon styles for tc-icons - must override icon-font.css defaults (1.5rem/24px)
const NAV_ICON_STYLE = {
  width: TC_ICON_SIZE,
  height: TC_ICON_SIZE,
  ['font-size']: TC_ICON_SIZE,
  ['line-height']: '1',
  ['vertical-align']: 'middle',
  color: 'var(--modus-wc-color-base-content, #171C1E)',
} as const;

const HEADER_ICON_STYLE = {
  width: TC_ICON_SIZE,
  height: TC_ICON_SIZE,
  ['font-size']: TC_ICON_SIZE,
  display: 'inline-flex',
  ['align-items']: 'center',
  ['justify-content']: 'center',
  color: 'white',
} as const;

// Button class constants
const NAV_BUTTON_CLASS =
  'w-8 h-8 border-none bg-transparent cursor-pointer flex items-center justify-center transition-all duration-200 p-0 hover:opacity-70 disabled:opacity-30 disabled:cursor-not-allowed';

const HEADER_NAV_BUTTON_CLASS =
  'w-8 h-[32px] border-none bg-transparent rounded-md cursor-pointer flex items-center justify-center transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed p-0 m-0 leading-none';

const DROPDOWN_BUTTON_CLASS =
  'flex items-center justify-center gap-1 px-3 py-2 bg-transparent text-white font-bold text-sm border-none cursor-pointer hover:bg-white/10 rounded w-[75px] h-[32px]';

export const Calendar: Component<CalendarProps> = (props) => {
  const [isOpen, setIsOpen] = createSignal(false);
  const [isMonthDropdownOpen, setIsMonthDropdownOpen] = createSignal(false);
  const [isYearDropdownOpen, setIsYearDropdownOpen] = createSignal(false);
  const [calendarRef, setCalendarRef] = createSignal<HTMLDivElement | null>(
    null
  );
  const [monthDropdownRef, setMonthDropdownRef] =
    createSignal<HTMLDivElement | null>(null);
  const [yearDropdownRef, setYearDropdownRef] =
    createSignal<HTMLDivElement | null>(null);
  const [monthDropdownMenuRef, setMonthDropdownMenuRef] =
    createSignal<HTMLDivElement | null>(null);
  const [yearDropdownMenuRef, setYearDropdownMenuRef] =
    createSignal<HTMLDivElement | null>(null);

  // Click outside to close calendar
  createEffect(() => {
    if (!isOpen()) return;

    const handleClickOutside = (event: Event): void => {
      const target = event.target as HTMLElement;
      const calendar = calendarRef();
      const monthDropdown = monthDropdownRef();
      const yearDropdown = yearDropdownRef();
      const monthDropdownMenu = monthDropdownMenuRef();
      const yearDropdownMenu = yearDropdownMenuRef();

      // Check if clicking inside calendar or dropdowns
      const isInsideCalendar = calendar && calendar.contains(target);
      const isInsideMonthDropdown =
        monthDropdown && monthDropdown.contains(target);
      const isInsideYearDropdown =
        yearDropdown && yearDropdown.contains(target);
      const isInsideMonthDropdownMenu =
        monthDropdownMenu && monthDropdownMenu.contains(target);
      const isInsideYearDropdownMenu =
        yearDropdownMenu && yearDropdownMenu.contains(target);

      // Don't close if clicking inside calendar or any dropdown
      if (
        isInsideCalendar ||
        isInsideMonthDropdown ||
        isInsideYearDropdown ||
        isInsideMonthDropdownMenu ||
        isInsideYearDropdownMenu
      ) {
        return;
      }

      setIsOpen(false);
    };

    // Use setTimeout to let other handlers run first
    setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 0);

    onCleanup(() => {
      document.removeEventListener('click', handleClickOutside);
    });
  });

  // Close month dropdown when clicking outside of it but inside calendar
  createEffect(() => {
    if (!isMonthDropdownOpen() || !isOpen()) return;

    const handleClickInsideCalendar = (event: Event): void => {
      const target = event.target as Node;
      const dropdown = monthDropdownRef();
      const calendar = calendarRef();

      // Only close if clicking inside calendar but outside dropdown
      if (
        calendar &&
        calendar.contains(target) &&
        dropdown &&
        !dropdown.contains(target)
      ) {
        setIsMonthDropdownOpen(false);
      }
    };

    document.addEventListener('click', handleClickInsideCalendar, true);

    onCleanup(() => {
      document.removeEventListener('click', handleClickInsideCalendar, true);
    });
  });

  // Close year dropdown when clicking outside of it but inside calendar
  createEffect(() => {
    if (!isYearDropdownOpen() || !isOpen()) return;

    const handleClickInsideCalendar = (event: Event): void => {
      const target = event.target as Node;
      const dropdown = yearDropdownRef();
      const calendar = calendarRef();

      // Only close if clicking inside calendar but outside dropdown
      if (
        calendar &&
        calendar.contains(target) &&
        dropdown &&
        !dropdown.contains(target)
      ) {
        setIsYearDropdownOpen(false);
      }
    };

    document.addEventListener('click', handleClickInsideCalendar, true);

    onCleanup(() => {
      document.removeEventListener('click', handleClickInsideCalendar, true);
    });
  });

  // Helper function to extract date from CalendarItem
  const getDateFromItem = (item: CalendarItem): Date => {
    if (item instanceof Date) {
      return item;
    }

    // Handle DiscreteDateTime with dateTime property (supports ISO strings)
    const dateTime = item.dateTime;
    return typeof dateTime === 'string' ? new Date(dateTime) : dateTime;
  };

  // Get available dates based on mode
  const getAvailableDates = (): Date[] => {
    const mode = props.mode ?? 'discrete';

    if (mode === 'regular') {
      return props.availableDates.map(getDateFromItem);
    } else {
      return props.availableDates
        .filter((item) => {
          if (isDiscreteDateTime(item)) {
            // If no id prop provided, show all discrete dates
            return props.id ? item.id === props.id : true;
          }
          return true; // For simple Date items, include them all
        })
        .map(getDateFromItem);
    }
  };

  // Helper function to find ALL items for a given date
  const findAllItemsByDate = (date: Date): CalendarItem[] => {
    return props.availableDates.filter((item) => {
      const itemDate = getDateFromItem(item);
      return isSameDay(itemDate, date);
    });
  };

  // Helper function to count items for a given date
  const getItemCountForDate = (date: Date): number => {
    return findAllItemsByDate(date).length;
  };

  // Initialize with the selected date's month/year
  const [currentMonth, setCurrentMonth] = createSignal(0);
  const [currentYear, setCurrentYear] = createSignal(0);

  // Reset calendar view when group/mode changes or selected date changes
  createEffect(() => {
    // When group/mode changes, go to earliest available date
    // When selected date changes, go to selected date's month
    const selectedDate = props.selectedDate;
    setCurrentMonth(selectedDate.getMonth());
    setCurrentYear(selectedDate.getFullYear());
  });

  // Helper functions
  const isSameDay = (date1: Date, date2: Date): boolean => {
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  };

  const isDateAvailable = (date: Date): boolean => {
    const mode = props.mode ?? 'discrete';

    if (mode === 'regular') {
      return isDateInRange(date);
    } else {
      const availableDates = getAvailableDates();
      return availableDates.some((availableDate) =>
        isSameDay(date, availableDate)
      );
    }
  };

  const isDateInRange = (date: Date): boolean => {
    // If no dateRange provided, all dates are considered in range
    if (!props.dateRange) {
      return true;
    }

    // Compare dates only, not exact timestamps
    const dateOnly = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate()
    );
    const startOnly = new Date(
      props.dateRange.start.getFullYear(),
      props.dateRange.start.getMonth(),
      props.dateRange.start.getDate()
    );
    const endOnly = new Date(
      props.dateRange.end.getFullYear(),
      props.dateRange.end.getMonth(),
      props.dateRange.end.getDate()
    );

    return dateOnly >= startOnly && dateOnly <= endOnly;
  };

  const findNearestAvailableDate = (
    direction: 'prev' | 'next'
  ): CalendarItem | null => {
    const mode = props.mode ?? 'discrete';

    if (mode === 'regular') {
      const current = new Date(props.selectedDate);
      if (direction === 'next') {
        current.setMonth(current.getMonth() + 1);
        // If no dateRange provided, allow navigation
        if (!props.dateRange || current <= props.dateRange.end) {
          return current;
        }
        return null;
      } else {
        current.setMonth(current.getMonth() - 1);
        // If no dateRange provided, allow navigation
        if (!props.dateRange || current >= props.dateRange.start) {
          return current;
        }
        return null;
      }
    } else {
      const availableItems = props.availableDates.filter((item) => {
        if (isDiscreteDateTime(item)) {
          // If no id prop provided, show all discrete dates
          return props.id ? item.id === props.id : true;
        }
        return true;
      });
      const sortedItems = [...availableItems].sort((a, b) => {
        const dateA = getDateFromItem(a);
        const dateB = getDateFromItem(b);
        return dateA.getTime() - dateB.getTime();
      });
      const currentSelected = props.selectedDate;

      if (direction === 'next') {
        return (
          sortedItems.find((item) => {
            const itemDate = getDateFromItem(item);
            return itemDate.getTime() > currentSelected.getTime();
          }) || null
        );
      } else {
        return (
          sortedItems.reverse().find((item) => {
            const itemDate = getDateFromItem(item);
            return itemDate.getTime() < currentSelected.getTime();
          }) || null
        );
      }
    }
  };

  const navigateToNearestDate = (direction: 'prev' | 'next'): void => {
    const nearestItem = findNearestAvailableDate(direction);
    if (nearestItem) {
      props.onDateSelect(nearestItem);
    }
  };

  // Check if month navigation should be disabled
  const canNavigateToPrevMonth = (): boolean => {
    const mode = props.mode ?? 'discrete';

    if (mode === 'regular') {
      // If no dateRange provided, allow navigation
      if (!props.dateRange) {
        return true;
      }
      const testDate = new Date(currentYear(), currentMonth() - 1, 1);
      return testDate >= props.dateRange.start;
    } else {
      const availableDates = getAvailableDates();
      if (availableDates.length === 0) return false;

      const sortedDates = [...availableDates].sort(
        (a, b) => a.getTime() - b.getTime()
      );
      const earliestDate = sortedDates[0];

      let prevMonth = currentMonth() - 1;
      let prevYear = currentYear();
      if (prevMonth < 0) {
        prevMonth = DECEMBER_MONTH;
        prevYear -= 1;
      }

      return (
        earliestDate.getFullYear() < prevYear ||
        (earliestDate.getFullYear() === prevYear &&
          earliestDate.getMonth() <= prevMonth)
      );
    }
  };

  const canNavigateToNextMonth = (): boolean => {
    const mode = props.mode ?? 'discrete';

    if (mode === 'regular') {
      // If no dateRange provided, allow navigation
      if (!props.dateRange) {
        return true;
      }
      const testDate = new Date(currentYear(), currentMonth() + 1, 1);
      return testDate <= props.dateRange.end;
    } else {
      const availableDates = getAvailableDates();
      if (availableDates.length === 0) return false;

      const sortedDates = [...availableDates].sort(
        (a, b) => b.getTime() - a.getTime()
      );
      const latestDate = sortedDates[0];

      let nextMonth = currentMonth() + 1;
      let nextYear = currentYear();
      if (nextMonth > DECEMBER_MONTH) {
        nextMonth = 0;
        nextYear += 1;
      }

      return (
        latestDate.getFullYear() > nextYear ||
        (latestDate.getFullYear() === nextYear &&
          latestDate.getMonth() >= nextMonth)
      );
    }
  };

  const navigateMonth = (direction: 'prev' | 'next'): void => {
    const mode = props.mode ?? 'discrete';

    if (mode === 'regular') {
      // In regular mode, navigate months freely
      if (direction === 'next') {
        let newMonth = currentMonth() + 1;
        let newYear = currentYear();
        if (newMonth > DECEMBER_MONTH) {
          newMonth = 0;
          newYear += 1;
        }
        setCurrentMonth(newMonth);
        setCurrentYear(newYear);
      } else {
        let newMonth = currentMonth() - 1;
        let newYear = currentYear();
        if (newMonth < 0) {
          newMonth = DECEMBER_MONTH;
          newYear -= 1;
        }
        setCurrentMonth(newMonth);
        setCurrentYear(newYear);
      }
    } else {
      // In discrete mode, navigate to the nearest month with available dates
      const availableDates = getAvailableDates();
      const currentViewMonth = currentMonth();
      const currentViewYear = currentYear();

      if (direction === 'next') {
        // Find next available date in a different month
        const nextDate = availableDates.find((date) => {
          return (
            date.getFullYear() > currentViewYear ||
            (date.getFullYear() === currentViewYear &&
              date.getMonth() > currentViewMonth)
          );
        });
        if (nextDate) {
          setCurrentMonth(nextDate.getMonth());
          setCurrentYear(nextDate.getFullYear());
        }
      } else {
        // Find previous available date in a different month
        const prevDate = availableDates.reverse().find((date) => {
          return (
            date.getFullYear() < currentViewYear ||
            (date.getFullYear() === currentViewYear &&
              date.getMonth() < currentViewMonth)
          );
        });
        if (prevDate) {
          setCurrentMonth(prevDate.getMonth());
          setCurrentYear(prevDate.getFullYear());
        }
      }
    }
  };

  // Calendar grid generation
  const calendarDays = createMemo(() => {
    const firstDay = new Date(currentYear(), currentMonth(), 1);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const days: Date[] = [];
    const current = new Date(startDate);

    for (let i = 0; i < CALENDAR_GRID_SIZE; i++) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }

    return days;
  });

  const handleDateClick = (date: Date): void => {
    if (isDateAvailable(date) && isDateInRange(date)) {
      const mode = props.mode ?? 'discrete';

      if (mode === 'regular') {
        // In regular mode, just pass the date directly
        props.onDateSelect(date);
        setCurrentMonth(date.getMonth());
        setCurrentYear(date.getFullYear());
        setIsOpen(false);
      } else {
        // In discrete mode, find the matching items
        const allItemsForDate = findAllItemsByDate(date);
        if (allItemsForDate.length > 0) {
          // If multiple items exist for the same date, pass all of them
          // If only one item exists, pass it as a single item (not array) for backward compatibility
          const selectedItem =
            allItemsForDate.length === 1 ? allItemsForDate[0] : allItemsForDate;
          props.onDateSelect(selectedItem);
          setCurrentMonth(date.getMonth());
          setCurrentYear(date.getFullYear());
          setIsOpen(false);
        }
      }
    }
  };

  const formatSelectedDate = (): string => {
    const date = props.selectedDate;
    const monthNames = [
      props.translations.monthsAbbreviated.january,
      props.translations.monthsAbbreviated.february,
      props.translations.monthsAbbreviated.march,
      props.translations.monthsAbbreviated.april,
      props.translations.monthsAbbreviated.may,
      props.translations.monthsAbbreviated.june,
      props.translations.monthsAbbreviated.july,
      props.translations.monthsAbbreviated.august,
      props.translations.monthsAbbreviated.september,
      props.translations.monthsAbbreviated.october,
      props.translations.monthsAbbreviated.november,
      props.translations.monthsAbbreviated.december,
    ];
    const month = monthNames[date.getMonth()];
    const day = date.getDate().toString();
    const year = date.getFullYear().toString();

    return `${month} ${day}, ${year}`;
  };

  // Helper to get abbreviated month names for dropdown selector button
  const getMonthNamesAbbreviated = (): string[] => [
    props.translations.monthsAbbreviated.january,
    props.translations.monthsAbbreviated.february,
    props.translations.monthsAbbreviated.march,
    props.translations.monthsAbbreviated.april,
    props.translations.monthsAbbreviated.may,
    props.translations.monthsAbbreviated.june,
    props.translations.monthsAbbreviated.july,
    props.translations.monthsAbbreviated.august,
    props.translations.monthsAbbreviated.september,
    props.translations.monthsAbbreviated.october,
    props.translations.monthsAbbreviated.november,
    props.translations.monthsAbbreviated.december,
  ];

  // Helper to get full month names for dropdown menu items
  const getMonthNamesFull = (): string[] => [
    props.translations.monthsFull.january,
    props.translations.monthsFull.february,
    props.translations.monthsFull.march,
    props.translations.monthsFull.april,
    props.translations.monthsFull.may,
    props.translations.monthsFull.june,
    props.translations.monthsFull.july,
    props.translations.monthsFull.august,
    props.translations.monthsFull.september,
    props.translations.monthsFull.october,
    props.translations.monthsFull.november,
    props.translations.monthsFull.december,
  ];

  // Helper to get abbreviated weekday names for calendar grid header
  const getWeekdayNames = (): string[] => [
    props.translations.weekdaysAbbreviated.sunday,
    props.translations.weekdaysAbbreviated.monday,
    props.translations.weekdaysAbbreviated.tuesday,
    props.translations.weekdaysAbbreviated.wednesday,
    props.translations.weekdaysAbbreviated.thursday,
    props.translations.weekdaysAbbreviated.friday,
    props.translations.weekdaysAbbreviated.saturday,
  ];

  // Helper to get available months for dropdown in discrete mode
  const getAvailableMonthsForDropdown = (): Array<{
    fullName: string;
    index: number;
  }> => {
    const year = currentYear();
    const allMonths = getMonthNamesFull();

    if (props.mode === 'discrete') {
      // Filter to only show months that have available dates
      return allMonths
        .map((fullName, idx) => ({ fullName, index: idx }))
        .filter(({ index }) => isMonthAvailable(index, year));
    } else {
      // In regular mode, show all months
      return allMonths.map((fullName, idx) => ({ fullName, index: idx }));
    }
  };

  // Helper to get available years based on mode and constraints
  const getAvailableYears = (): number[] => {
    const years = new Set<number>();

    if (props.mode === 'regular' && props.dateRange) {
      // In regular mode, generate years based on date range
      const startYear = props.dateRange.start.getFullYear();
      const endYear = props.dateRange.end.getFullYear();
      for (let year = startYear; year <= endYear; year++) {
        years.add(year);
      }
    } else if (props.mode === 'discrete') {
      // In discrete mode, get years from available dates
      const availableDates = getAvailableDates();
      availableDates.forEach((date) => {
        years.add(date.getFullYear());
      });
    }

    // If no years found (e.g., no constraints), provide a reasonable range
    if (years.size === 0) {
      const currentYear = new Date().getFullYear();
      const defaultStart = currentYear - DEFAULT_YEAR_RANGE_OFFSET;
      const defaultEnd = currentYear + DEFAULT_YEAR_RANGE_OFFSET;

      const startYear = props.yearRange?.start ?? defaultStart;
      const endYear = props.yearRange?.end ?? defaultEnd;

      for (let year = startYear; year <= endYear; year++) {
        years.add(year);
      }
    }

    return Array.from(years).sort((a, b) => a - b);
  };

  // Helper to check if a month/year combination has available dates
  const isMonthAvailable = (monthIndex: number, year: number): boolean => {
    if (props.mode === 'regular' && props.dateRange) {
      // Check if any day in the month falls within the date range
      const firstDayOfMonth = new Date(year, monthIndex, 1);
      const lastDayOfMonth = new Date(year, monthIndex + 1, 0);
      return (
        firstDayOfMonth <= props.dateRange.end &&
        lastDayOfMonth >= props.dateRange.start
      );
    } else if (props.mode === 'discrete') {
      // Check if any available dates are in this month/year
      const availableDates = getAvailableDates();
      return availableDates.some(
        (date) => date.getMonth() === monthIndex && date.getFullYear() === year
      );
    }
    return true;
  };

  return (
    <div ref={setCalendarRef} class="relative w-fit">
      {/* Calendar Trigger */}
      <div
        class={`flex items-center justify-between w-48 h-10 rounded-lg transition-all duration-200 gap-2 px-3 py-2 bg-white shadow-lg border-none ${props.disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
        onClick={() => !props.disabled && setIsOpen(!isOpen())}
      >
        <div class="w-fit">
          <TooltipDelayed
            text={props.translations.previousAvailable}
            position="left"
            disabled={!findNearestAvailableDate('prev') || props.disabled}
          >
            <button
              class={NAV_BUTTON_CLASS}
              onClick={(e) => {
                e.stopPropagation();
                navigateToNearestDate('prev');
              }}
              disabled={!findNearestAvailableDate('prev') || props.disabled}
            >
              <span
                class="tc-icon-arrow-line-back icon-font"
                aria-hidden="true"
                style={NAV_ICON_STYLE}
              />
            </button>
          </TooltipDelayed>
        </div>
        <span class="flex-1 font-semibold text-sm text-center text-trimble-gray-500 flex items-center justify-center mb-0.5 whitespace-nowrap">
          {formatSelectedDate()}
        </span>
        <div class="w-fit">
          <TooltipDelayed
            text={props.translations.nextAvailable}
            position="right"
            disabled={!findNearestAvailableDate('next') || props.disabled}
          >
            <button
              class={NAV_BUTTON_CLASS}
              onClick={(e) => {
                e.stopPropagation();
                navigateToNearestDate('next');
              }}
              disabled={!findNearestAvailableDate('next') || props.disabled}
            >
              <span
                class="tc-icon-arrow-line-forward icon-font"
                aria-hidden="true"
                style={NAV_ICON_STYLE}
              />
            </button>
          </TooltipDelayed>
        </div>
      </div>

      {/* Calendar Dropdown */}
      <Show when={isOpen()}>
        <div class="absolute bottom-full mb-2 w-[273px] h-[327px] rounded z-50 shadow-xl bg-white -left-12 animate-in slide-in-from-bottom-2 duration-200 origin-bottom">
          <div
            class="bg-blue flex items-center justify-between w-full h-[55px] px-6 rounded-t"
            // eslint-disable-next-line @typescript-eslint/naming-convention -- Non-standard padding value for precise spacing
            style={{ 'padding-top': '11.5px', 'padding-bottom': '11.5px' }}
          >
            <TooltipDelayed
              text={props.translations.previousMonth}
              position="left"
              disabled={!canNavigateToPrevMonth()}
            >
              <button
                class={HEADER_NAV_BUTTON_CLASS}
                onClick={() => {
                  navigateMonth('prev');
                }}
                disabled={!canNavigateToPrevMonth()}
              >
                <span
                  class="tc-icon-chevron-left icon-font"
                  aria-hidden="true"
                  style={HEADER_ICON_STYLE}
                />
              </button>
            </TooltipDelayed>

            <div class="flex items-center justify-center gap-2">
              {/* Month Dropdown */}
              <div ref={setMonthDropdownRef} class="relative">
                <button
                  class={DROPDOWN_BUTTON_CLASS}
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsMonthDropdownOpen(!isMonthDropdownOpen());
                    setIsYearDropdownOpen(false);
                  }}
                >
                  <span>{getMonthNamesAbbreviated()[currentMonth()]}</span>
                  <span
                    class={`tc-icon-${isMonthDropdownOpen() ? 'chevron-up' : 'chevron-down'} icon-font`}
                    aria-hidden="true"
                    style={HEADER_ICON_STYLE}
                  />
                </button>
                <Show when={isMonthDropdownOpen()}>
                  <div
                    ref={setMonthDropdownMenuRef}
                    class="absolute top-full left-0 mt-[12px] bg-white rounded shadow-lg z-[100] max-h-[184px] overflow-y-auto w-[104px]"
                  >
                    <For each={getAvailableMonthsForDropdown()}>
                      {(monthData) => {
                        const isSelected = monthData.index === currentMonth();

                        let buttonRef: HTMLButtonElement | undefined;

                        // Scroll to selected item when it mounts
                        createEffect(() => {
                          if (isSelected && buttonRef) {
                            buttonRef.scrollIntoView({ block: 'nearest' });
                          }
                        });

                        return (
                          <button
                            ref={buttonRef}
                            class={`w-full h-[40px] text-left text-sm border-none cursor-pointer transition-colors py-2 px-4 ${
                              isSelected
                                ? 'bg-blue-pale text-blue font-semibold'
                                : 'text-gray-8 hover:bg-gray-light'
                            }`}
                            onClick={() => {
                              setCurrentMonth(monthData.index);
                              setIsMonthDropdownOpen(false);
                            }}
                          >
                            {monthData.fullName}
                          </button>
                        );
                      }}
                    </For>
                  </div>
                </Show>
              </div>

              {/* Year Dropdown */}
              <div ref={setYearDropdownRef} class="relative">
                <button
                  class={DROPDOWN_BUTTON_CLASS}
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsYearDropdownOpen(!isYearDropdownOpen());
                    setIsMonthDropdownOpen(false);
                  }}
                >
                  <span>{currentYear()}</span>
                  <span
                    class={`tc-icon-${isYearDropdownOpen() ? 'chevron-up' : 'chevron-down'} icon-font`}
                    aria-hidden="true"
                    style={HEADER_ICON_STYLE}
                  />
                </button>
                <Show when={isYearDropdownOpen()}>
                  <div
                    ref={setYearDropdownMenuRef}
                    class="absolute top-full left-0 mt-[11px] bg-white rounded shadow-lg z-[100] max-h-[184px] overflow-y-auto w-[104px]"
                  >
                    <For each={getAvailableYears()}>
                      {(year) => {
                        const isSelected = year === currentYear();

                        let buttonRef: HTMLButtonElement | undefined;

                        // Scroll to selected item when it mounts
                        createEffect(() => {
                          if (isSelected && buttonRef) {
                            buttonRef.scrollIntoView({ block: 'nearest' });
                          }
                        });

                        return (
                          <button
                            ref={buttonRef}
                            class={`w-full h-[40px] text-left text-sm border-none cursor-pointer transition-colors py-2 px-4 ${
                              isSelected
                                ? 'bg-blue-pale text-blue font-semibold'
                                : 'text-gray-8 hover:bg-gray-light'
                            }`}
                            onClick={() => {
                              setCurrentYear(year);
                              setIsYearDropdownOpen(false);
                            }}
                          >
                            {year}
                          </button>
                        );
                      }}
                    </For>
                  </div>
                </Show>
              </div>
            </div>

            <TooltipDelayed
              text={props.translations.nextMonth}
              position="right"
              disabled={!canNavigateToNextMonth()}
            >
              <button
                class={HEADER_NAV_BUTTON_CLASS}
                onClick={() => {
                  navigateMonth('next');
                }}
                disabled={!canNavigateToNextMonth()}
              >
                <span
                  class="tc-icon-chevron-right icon-font"
                  aria-hidden="true"
                  style={HEADER_ICON_STYLE}
                />
              </button>
            </TooltipDelayed>
          </div>

          <div class="grid grid-cols-7 gap-0 bg-white px-6 pt-4 pb-2">
            <For each={getWeekdayNames()}>
              {(day) => (
                <div
                  class="w-8 h-6 flex items-center justify-center text-center text-sm font-semibold"
                  style={{
                    color: 'var(--modus-wc-color-base-content, #171C1E)',
                  }}
                >
                  {day}
                </div>
              )}
            </For>
          </div>

          <div class="grid grid-cols-7 gap-y-[1px] px-6 pb-6 bg-white rounded-b overflow-visible">
            <For each={calendarDays()}>
              {(date) => {
                const isCurrentMonth = date.getMonth() === currentMonth();
                const isAvailable = isDateAvailable(date);
                const isInRange = isDateInRange(date);
                const isSelected = isSameDay(date, props.selectedDate);
                const isInteractive =
                  (isCurrentMonth && isAvailable && isInRange) || isSelected;

                const itemCount = getItemCountForDate(date);
                const showBadge =
                  itemCount > 1 && isCurrentMonth && isAvailable;

                const tooltipText = isSelected
                  ? props.translations.selectedDateTooltip
                  : props.translations.availableDateTooltip;
                const mode = props.mode ?? 'discrete';
                const shouldShowTooltip = mode === 'discrete' && isInteractive;

                return (
                  <div class="relative w-8 h-8 flex items-center justify-center">
                    <TooltipDelayed
                      text={tooltipText}
                      position="right"
                      disabled={!shouldShowTooltip}
                    >
                      <button
                        class={`w-8 h-8 border-none rounded-full flex items-center justify-center text-sm font-medium transition-all duration-200 relative ${
                          isCurrentMonth ? '' : 'text-gray-3'
                        } ${
                          isInteractive
                            ? 'cursor-pointer'
                            : 'text-gray-4 cursor-not-allowed'
                        } ${
                          isSelected
                            ? 'bg-blue text-white font-bold'
                            : isInteractive && isCurrentMonth
                              ? props.mode === 'discrete'
                                ? 'bg-blue-pale text-black font-bold font-sans text-sm leading-5 tracking-wide text-center hover:bg-blue-400 hover:text-white'
                                : 'text-black font-normal font-sans text-sm leading-5 tracking-wide text-center hover:bg-blue-400 hover:text-white'
                              : 'bg-transparent'
                        }`}
                        onClick={() => {
                          handleDateClick(date);
                        }}
                        disabled={!isInteractive}
                      >
                        {date.getDate()}
                      </button>
                    </TooltipDelayed>

                    {/* Notification badge for multiple entries */}
                    <Show when={showBadge}>
                      <div
                        class="absolute flex items-center justify-center text-white text-xs font-bold rounded-[45px] bg-trimble-gray-475"
                        style={{
                          width: '20px',
                          height: '20px',
                          top: '-9px',
                          left: '19.5px',
                          opacity: '1',
                        }}
                      >
                        {itemCount}
                      </div>
                    </Show>
                  </div>
                );
              }}
            </For>
          </div>
        </div>
      </Show>
    </div>
  );
};
